import { useRouteMatcher, useRoutes, useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import isFunction from 'lodash-es/isFunction';
import { useState } from 'react';

import { useCurrentDocument } from '../../hooks/useDocument';

import { useApi } from '../../contexts/ApiContext';
import { useHasPermission } from '../../contexts/AuthContext';
import BasicButton from '../buttons/BasicButton';
import ShareModal from '../popups/ShareModal';
import Menu from './Menu';

import styles from '../../styles/menus/admin-menu.module.css';

interface AdminMenuProps {
    className?: string;
    buttonClassName?: string;
}

function AdminMenu({ className, buttonClassName }: AdminMenuProps) {
    const routes = useRoutes();
    const url = useUrlGenerator();
    const routeMatcher = useRouteMatcher();
    const [opened, setOpened] = useState(false);
    const api = useApi();
    const { baseUrl: apiBaseUrl = null } = api;
    const currentDocument = useCurrentDocument();
    const { type: documentType = null } = currentDocument || {};

    const [shareOpened, setShareOpened] = useState(false);
    const onClickShare = (e) => {
        e.preventDefault();
        setShareOpened(true);
    };
    const onShareClosed = () => {
        setShareOpened(false);
    };
    const canEditDocument = useHasPermission('edit_document');
    const canExportContest = useHasPermission('export_contest');
    const canExportForm = useHasPermission('export_form');

    const items = [
        {
            id: 'share',
            label: 'Partage',
            route: routes.document,
            href: ({ type, slug }) => url('document.share', { type, slug }),
            onClick: onClickShare,
        },
        canEditDocument
            ? {
                  id: 'edit',
                  label: 'Modifier',
                  route: routes.document,
                  href: (params, { identifier = '' }) =>
                      `https://wordpress.urbania.ca/wp-admin/post.php?post=${identifier.replace(/^wordpress-/, '')}&action=edit`,
                  external: true,
              }
            : null,
        canExportContest
            ? {
                  id: 'export-contest',
                  label: 'Exporter',
                  route: routes.contest,
                  href: (params, { id }) => `${apiBaseUrl}/contests/${id}.csv`,
                  external: true,
              }
            : null,
        canExportForm
            ? {
                  id: 'export-form',
                  label: 'Exporter',
                  check: (params, { type = null, form = null, blocks = null }) => {
                      const finalForm =
                          (type !== 'contest' ? form : null) ||
                          (blocks || []).find(({ form = null }) => form !== null) ||
                          null;
                      return finalForm !== null;
                  },
                  href: (params, { form = null, blocks = null }) => {
                      const finalForm =
                          form ||
                          (blocks || []).reduce(
                              (currentForm, { form = null }) => currentForm || form,
                              null,
                          ) ||
                          null;
                      return `${apiBaseUrl}/forms/${finalForm.id}.csv`;
                  },

                  external: true,
              }
            : null,
    ]
        .filter((it) => it !== null)
        .map(({ route = null, check = null, href, ...item }) => {
            const [match = false, params] = route !== null ? routeMatcher(route) : [true, {}];
            if (!match || (check !== null && !check(params, currentDocument || {}))) {
                return null;
            }
            return {
                ...item,
                href: isFunction(href) ? href(params, currentDocument || {}) : href,
            };
        })
        .filter((item) => item !== null);

    const isShareOnly = items.length === 1 && items[0].id === 'share';
    // const isShareOnly = true;
    const onClickOpen = () => {
        if (isShareOnly) {
            setShareOpened(true);
            return;
        }
        setOpened(!opened);
    };

    return (
        <div
            className={classNames([
                styles.container,
                'admin-menu',
                {
                    [styles.shareOnly]: isShareOnly,
                    [styles.transparent]:
                        documentType === 'video' ||
                        documentType === 'podcast' ||
                        documentType === 'micromag',
                    [styles.hidden]: items.length === 0,
                    [styles.opened]: opened,
                },
                className,
            ])}
        >
            <BasicButton
                className={classNames([styles.openButton, buttonClassName])}
                onClick={onClickOpen}
            >
                <span className={classNames(['material-symbols-rounded', styles.icon])}>
                    {isShareOnly ? 'share' : opened ? 'close' : 'settings'}
                </span>
            </BasicButton>
            <Menu
                items={items}
                className={styles.menu}
                itemsClassName={styles.items}
                itemClassName={styles.item}
                buttonClassName={styles.button}
            />
            {shareOpened ? (
                <ShareModal document={currentDocument} onClosed={onShareClosed} />
            ) : null}
        </div>
    );
}

export default AdminMenu;
